import { useEffect, useState } from "react";
import { TextField } from "@/components/forms/fields";
import { InfoIcon } from "@/components/Icons/InfoIcon";
import { SimpleTooltip } from "@/components/Common/Tooltip/SimpleTooltip";
import { SectionHeader } from "@/components/Common/SectionHeader/SectionHeader";
import { Button } from "@/components/ui/button";
import { SimpleClusterForm } from "@/types";
import { useFormikContext } from "formik";

import { RECIPES } from "./RecipesSection.constants";

// Reserved for when interval/timeout/grace period fields are re-enabled.
// function parseWholeSeconds(value: string | number | undefined): number | null {
//   if (value === "" || value === undefined || value === null) return null;
//   const raw = typeof value === "number" ? String(value) : String(value).trim();
//   if (raw === "") return null;
//   if (!/^-?\d+$/.test(raw)) return null;
//   const n = parseInt(raw, 10);
//   return Number.isFinite(n) ? n : null;
// }

function FieldLabelWithTooltip({
  label,
  tooltip,
  tooltipId
}: {
  label: string;
  tooltip: string;
  tooltipId: string;
  optional?: boolean;
}) {
  return (
    <div className="mb-2 flex items-center gap-1.5">
      <span className="subtext-small text-gray-dark-2200 dark:text-gray-dark-1000">{label}</span>
      <SimpleTooltip
        id={tooltipId}
        content={tooltip}
        place="top"
        tooltipClassName="max-w-[500px] whitespace-normal"
      >
        <button
          type="button"
          className="flex border-none bg-transparent p-0"
          aria-label={`More about ${label}`}
        >
          <InfoIcon className="h-4 w-4 cursor-pointer text-gray-dark-1000" />
        </button>
      </SimpleTooltip>
    </div>
  );
}

function HealthCheckHeaderStatus({
  path,
  isRecommended,
  expanded,
  onExpand
}: {
  path: string;
  isRecommended: boolean;
  expanded: boolean;
  onExpand: () => void;
}) {
  return (
    <div className="flex max-w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-[8px] border border-border bg-muted/30 px-3 py-2">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 subtext-small">
        <span className="text-muted-foreground">Health check</span>
        {path ? (
          <>
            <span className="font-medium text-foreground">{path}</span>
            {isRecommended && (
              <span className="text-muted-foreground">· recommended for this recipe</span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">Not set</span>
        )}
      </div>
      {!expanded && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto shrink-0 px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={onExpand}
        >
          Expand to edit
        </Button>
      )}
    </div>
  );
}

export const HealthCheckSection: React.FC = () => {
  const { touched, values } = useFormikContext<SimpleClusterForm>();
  const selectedRecipe = values.containerRecipe ? RECIPES[values.containerRecipe] : undefined;
  const path = typeof values.healthCheckPath === "string" ? values.healthCheckPath.trim() : "";
  const recommendedPath = selectedRecipe
    ? (selectedRecipe.healthCheckPath ?? "/health")
    : undefined;
  const isRecommended = Boolean(recommendedPath && path === recommendedPath);

  // Collapsed by default for both known and custom recipes.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [values.containerRecipe]);

  const validatePath = (value: string | undefined) => {
    if (!touched.healthCheckPath) return undefined;
    const trimmed = (value ?? "").trim();
    if (trimmed === "") return undefined;
    if (!trimmed.startsWith("/")) {
      return "Path must start with /";
    }
    if (trimmed.length > 256) {
      return "Path must be at most 256 characters";
    }
    if (trimmed.includes("${")) {
      return "Path cannot contain ${";
    }
    if (
      [...trimmed].some((ch) => {
        const code = ch.charCodeAt(0);
        return (code >= 0 && code <= 31) || code === 127;
      })
    ) {
      return "Path contains invalid characters";
    }
    if (/\s/.test(trimmed)) {
      return "Path cannot contain spaces";
    }
    if (!/^\/[a-zA-Z0-9/_.\-~!$&'()*+,;=:@%]*$/.test(trimmed)) {
      return "Use a valid URL path (letters, numbers, /, and common path characters)";
    }
    return undefined;
  };

  // Hidden for now — interval/timeout/grace period are not user-configurable and must not be sent to the API.
  // const validateInterval = (value: string | number | undefined) => {
  //   if (!touched.healthCheckIntervalSeconds) return undefined;
  //   const n = parseWholeSeconds(value);
  //   if (n === null) return undefined;
  //   if (n < 5 || n > 600) return "Use 5–600 seconds (5s–10m)";
  //   return undefined;
  // };

  // const validateTimeout = (value: string | number | undefined) => {
  //   if (!touched.healthCheckTimeoutSeconds) return undefined;
  //   const n = parseWholeSeconds(value);
  //   if (n === null) return undefined;
  //   if (n < 1 || n > 600) return "Use 1–600 seconds (1s–10m)";
  //   return undefined;
  // };

  // const validateGracePeriod = (value: string | number | undefined) => {
  //   if (!touched.healthCheckGracePeriodSeconds) return undefined;
  //   const n = parseWholeSeconds(value);
  //   if (n === null) return undefined;
  //   if (n < 10 || n > 3600) return "Use 10–3600 seconds (10s–1h)";
  //   return undefined;
  // };

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-row flex-wrap items-end justify-between gap-4">
        <SectionHeader
          title="Health Check (optional)"
          subtitle="Optionally monitor your application with an HTTP health check. We request your endpoint at regular intervals through the same route as your public traffic — a 2xx response means healthy. Your public URL is published once the check passes."
          savedInConfig
        />
        <HealthCheckHeaderStatus
          path={path}
          isRecommended={isRecommended}
          expanded={expanded}
          onExpand={() => setExpanded(true)}
        />
      </div>
      {expanded && (
        <div className="w-full rounded-[8px] border border-gray-dark-200 dark:border-gray-light-1900">
          <div className="flex flex-col gap-6 p-4">
            <div className="flex flex-col">
              <FieldLabelWithTooltip
                label="Path"
                tooltipId="tooltip-health-check-path"
                tooltip="GET endpoint that returns a successful (2xx) response when your application is ready. Checked every 10 seconds; must respond without authentication. Apps with long startups may show unhealthy until ready. Leave empty to disable health monitoring."
              />
              <TextField
                name="healthCheckPath"
                placeholder="/health"
                className="text-grey min-h-10 w-full"
                baseHOCClassName="pb-0"
                validate={validatePath}
              />
            </div>
            {/* <div className="flex flex-col">
            <FieldLabelWithTooltip
              label="Check interval (seconds)"
              optional
              tooltipId="tooltip-health-interval"
              tooltip="How often we send a GET to your path. Default 10s. Range 5s–10m."
            />
            <TextField
              type="number"
              name="healthCheckIntervalSeconds"
              placeholder="10"
              className="text-grey min-h-10 w-full"
              baseHOCClassName="pb-0"
              validate={validateInterval}
              disabledOnWheel
              min={5}
              max={600}
              step={1}
            />
          </div>
          <div className="flex flex-col">
            <FieldLabelWithTooltip
              label="Timeout (seconds)"
              optional
              tooltipId="tooltip-health-timeout"
              tooltip="How long we wait for one check to respond before counting it as failed. Default 10s. Use a higher value for slow-starting apps so healthy but slow responses are not marked failed. Range 1s–10m."
            />
            <TextField
              type="number"
              name="healthCheckTimeoutSeconds"
              placeholder="10"
              className="text-grey min-h-10 w-full"
              baseHOCClassName="pb-0"
              validate={validateTimeout}
              disabledOnWheel
              min={1}
              max={600}
              step={1}
            />
          </div>
          <div className="flex flex-col">
            <FieldLabelWithTooltip
              label="Grace period (seconds)"
              optional
              tooltipId="tooltip-health-grace-period"
              tooltip="Warm-up window after start before failed checks count toward restarts. Default 60s. Set long enough to cover boot (e.g. model load). Range 10s–1h."
            />
            <TextField
              type="number"
              name="healthCheckGracePeriodSeconds"
              placeholder="60"
              className="text-grey min-h-10 w-full"
              baseHOCClassName="pb-0"
              validate={validateGracePeriod}
              disabledOnWheel
              min={10}
              max={3600}
              step={1}
            />
          </div> */}
          </div>
        </div>
      )}
    </div>
  );
};
